import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const formatKpiValue = (key, value) => {
  if (['totalEventRevenue', 'lastEventRegistration', 'lastProfessionalRegistration'].includes(key)) {
    if (key === 'totalEventRevenue') return formatCurrency(value);
    return formatDate(value);
  }

  if (key === 'lastMemberRegistration') return formatDate(value);
  if (typeof value === 'number') return new Intl.NumberFormat('en-CH').format(value);
  return value || '—';
};

const maxValue = (items) => items.reduce((highest, item) => Math.max(highest, Number(item.value || 0)), 0);
const getBarWidth = (value, max) => {
  if (!value || value <= 0 || !max) return 0;
  return Math.max((Number(value) / Math.max(Number(max), 1)) * 100, 0);
};

const PIE_COLORS = ['#1B3047', '#14b8a6', '#d97706', '#8b5cf6', '#f97316', '#10b981', '#ef4444', '#0ea5e9'];

const buildPieStyle = (items) => {
  const filtered = (items || []).filter((item) => Number(item.value || 0) > 0);
  if (!filtered.length) {
    return { background: 'linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)' };
  }

  const total = filtered.reduce((sum, item) => sum + Number(item.value || 0), 0);
  let start = 0;
  const segments = filtered.map((item, index) => {
    const size = total > 0 ? (Number(item.value || 0) / total) * 100 : 0;
    const end = start + size;
    const color = PIE_COLORS[index % PIE_COLORS.length];
    const segment = `${color} ${start}% ${end}%`;
    start = end;
    return segment;
  });

  return {
    background: `conic-gradient(${segments.join(', ')})`,
  };
};

export default function DashboardManager() {
  const { strings } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/dashboardController.php`);
      const result = await response.json();

      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setDashboard(result.dashboard || null);
    } catch (err) {
      setError(err.message || 'Unable to load dashboard data.');
      setDashboard(null);
    }
  }, []);

  const loadProfessionalRecords = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/professionalsController.php`);
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setProfessionals(result.members || []);
    } catch (err) {
      setError((current) => current || err.message || 'Unable to load professionals.');
    }
  }, []);

  const loadActiveEvents = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/eventsController.php`);
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      const eventList = result.events || [];
      const activeEventsList = eventList.filter((event) => {
        const eventDate = event.startTime ? new Date(event.startTime) : null;
        return eventDate && eventDate >= new Date();
      });

      const detailedEvents = await Promise.all(
        activeEventsList.map(async (event) => {
          const registrationResponse = await fetch(`${BASE_URL}/eventRegistrationController.php?eventId=${event.id}`);
          const registrationResult = await registrationResponse.json();
          const registrations = registrationResponse.ok && registrationResult.ok ? (registrationResult.registrations || []) : [];
          const validRegistrations = registrations.filter((registration) => String(registration.registration_status || '').toLowerCase() !== 'cancelled');

          const latestRegistration = validRegistrations.reduce((latest, registration) => {
            const candidate = registration.registered_at ?? registration.created_at;
            if (!candidate) return latest;
            const current = new Date(candidate).getTime();
            if (!latest || current > new Date(latest).getTime()) {
              return candidate;
            }
            return latest;
          }, null);

          const revenue = validRegistrations.reduce((sum, registration) => sum + Number(registration.amount_paid || 0), 0);

          return {
            id: event.id,
            title: event.title || 'Untitled event',
            registrations: validRegistrations.length,
            revenue,
            lastRegistration: latestRegistration,
          };
        })
      );

      setActiveEvents(detailedEvents);
    } catch (err) {
      setError((current) => current || err.message || 'Unable to load active event stats.');
      setActiveEvents([]);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadProfessionalRecords(), loadActiveEvents()]);
      setLoading(false);
    };

    loadAll();
  }, [loadDashboard, loadProfessionalRecords, loadActiveEvents]);

  const kpis = useMemo(() => {
    const metrics = dashboard?.kpis || {};
    const eventRegistrations = dashboard?.charts?.dailyEventRegistrationsAndRevenue || [];
    const lastEventRegistration = eventRegistrations.reduce((latest, item) => {
      if (!item.date) return latest;
      const current = new Date(item.date).getTime();
      if (!latest || current > new Date(latest).getTime()) return item.date;
      return latest;
    }, null);

    const latestProfessional = professionals.reduce((latest, professional) => {
      const candidate = professional.createdAt ?? professional.created_at;
      if (!candidate) return latest;
      const current = new Date(candidate).getTime();
      if (!latest || current > new Date(latest).getTime()) return candidate;
      return latest;
    }, null);

    return [
      { key: 'totalRegisteredUsers', label: 'Total Registered Users', value: metrics.totalRegisteredUsers, className: 'theme-navy' },
      { key: 'totalProfessionals', label: 'Total Professionals', value: metrics.totalProfessionals, className: 'theme-mint' },
      { key: 'activeMembers', label: 'Active Members', value: metrics.activeMembers, className: 'theme-gold' },
      { key: 'lastMemberRegistration', label: 'Last Member Registration', value: metrics.lastMemberRegistration, className: 'theme-sand' },
      { key: 'totalEventRevenue', label: 'Total Event Revenue', value: metrics.totalEventRevenue, className: 'theme-rose' },
      { key: 'lastEventRegistration', label: 'Last Event Registration', value: lastEventRegistration, className: 'theme-indigo' },
      { key: 'lastProfessionalRegistration', label: 'Last Professional Registration', value: latestProfessional, className: 'theme-olive' },
    ];
  }, [dashboard, professionals]);

  const monthlyMembershipGrowth = dashboard?.charts?.monthlyMembershipGrowth || [];
  const dailyRegistrations = dashboard?.charts?.dailyEventRegistrationsAndRevenue || [];
  const languageBreakdown = dashboard?.charts?.professionalLanguageBreakdown || [];
  const motivationBreakdown = dashboard?.charts?.professionalMotivationsDistribution || [];
  const memberAudience = dashboard?.charts?.memberVsNonMemberEventAttendanceRatio || [];
  const geographicBreakdown = dashboard?.charts?.geographicDistribution || {};
  const totalDailyRegistrations = dailyRegistrations.reduce((sum, item) => sum + Number(item.totalRegistrations || 0), 0);
  const attendancePie = memberAudience.filter((entry) => Number(entry.value || 0) > 0);
  const professionalsByCanton = (geographicBreakdown.professionalsByCanton || []).filter((entry) => Number(entry.value || 0) > 0);
  const usersByState = (geographicBreakdown.usersByState || []).filter((entry) => Number(entry.value || 0) > 0);
  const monthGrowthPoints = useMemo(() => {
    const ordered = [...monthlyMembershipGrowth].sort((a, b) => a.month.localeCompare(b.month));
    const startMonth = new Date('2026-07-01');
    const allMonths = [];
    for (let i = 0; i < 12; i += 1) {
      const date = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const match = ordered.find((entry) => String(entry.month) === key);
      allMonths.push({
        month: key,
        count: Number(match?.count || 0),
      });
    }
    return allMonths;
  }, [monthlyMembershipGrowth]);
  const barChartMax = Math.max(1, ...monthGrowthPoints.map((entry) => Number(entry.count || 0)));
  const membershipBarColors = ['#1B3047', '#2D5C8A', '#4E7BB6', '#7CA6D9', '#A3C2EF', '#D9E8FF', '#1B3047', '#2D5C8A', '#4E7BB6', '#7CA6D9', '#A3C2EF', '#D9E8FF'];
  const getLocalizedName = (entry) => {
    if (!entry) return 'Unknown';
    const langCode = String(strings?.lang_code ?? strings?.lang ?? '').toUpperCase();
    if (langCode === 'RU' && entry?.name_ru) return entry.name_ru;
    if (entry?.name_en) return entry.name_en;
    if (entry?.label && entry.label !== entry?.key) return entry.label;
    if (entry?.name_ru) return entry.name_ru;
    return entry?.key || 'Unknown';
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Dashboard</h2>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-empty-state">Loading dashboard…</div>
      ) : !dashboard ? (
        <div className="admin-empty-state">No dashboard data available.</div>
      ) : (
        <>
          <div className="dashboard-kpis">
            {kpis.map((item) => (
              <div key={item.key} className={`dashboard-kpi-card ${item.className}`}>
                <span className="dashboard-kpi-label">{item.label}</span>
                <strong className="dashboard-kpi-value">{formatKpiValue(item.key, item.value)}</strong>
              </div>
            ))}
          </div>

          <section className="dashboard-panel dashboard-events-panel">
            <div className="dashboard-panel-header">
              <h3>Active Events</h3>
            </div>
            <div className="dashboard-event-grid">
              {activeEvents.length === 0 ? (
                <div className="admin-empty-state compact">No active events found.</div>
              ) : (
                activeEvents.map((event) => (
                  <div key={event.id} className="dashboard-event-card">
                    <div className="dashboard-event-card-header">
                      <h4>{event.title}</h4>
                    </div>
                    <div className="dashboard-event-metric-row">
                      <span>Registrations</span>
                      <strong>{event.registrations}</strong>
                    </div>
                    <div className="dashboard-event-metric-row">
                      <span>Revenue</span>
                      <strong>{formatCurrency(event.revenue)}</strong>
                    </div>
                    <div className="dashboard-event-metric-row">
                      <span>Last registration</span>
                      <strong>{formatDate(event.lastRegistration)}</strong>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <div className="dashboard-grid">
            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Monthly Membership Growth</h3>
              </div>
              {monthGrowthPoints.length === 0 ? (
                <div className="admin-empty-state compact">No monthly data yet.</div>
              ) : (
                <div className="dashboard-bar-chart-wrapper">
                  <div className="dashboard-bar-chart">
                    {monthGrowthPoints
                      .filter((entry) => Number(entry.count || 0) > 0)
                      .map((entry, index) => {
                        const height = Math.max((Number(entry.count || 0) / barChartMax) * 100, 8);
                        const monthName = new Date(`${entry.month}-01T00:00:00`).toLocaleString('en-US', { month: 'short' });

                        return (
                          <div key={entry.month} className="dashboard-month-bar-item">
                            <span className="dashboard-month-bar-value">{entry.count}</span>
                            <div className="dashboard-month-bar-column-wrap">
                              <div
                                className="dashboard-month-bar-column"
                                style={{
                                  height: `${height}%`,
                                  background: membershipBarColors[index % membershipBarColors.length],
                                }}
                              />
                            </div>
                            <span className="dashboard-month-bar-label">{monthName}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </section>

            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Daily Event Registrations & Revenue</h3>
              </div>
              <div className="dashboard-list">
                {dailyRegistrations.length === 0 ? (
                  <div className="admin-empty-state compact">No event registration activity yet.</div>
                ) : (
                  dailyRegistrations.map((entry) => (
                    <div key={entry.date} className="dashboard-list-item">
                      <span>{formatDate(entry.date)}</span>
                      <div className="dashboard-list-meta">
                        <span>{entry.totalRegistrations || 0} registrations</span>
                        <span>{formatCurrency(entry.revenue || 0)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Professional Language Breakdown</h3>
              </div>
              <div className="chart-bars">
                {languageBreakdown.length === 0 ? (
                  <div className="admin-empty-state compact">No language data yet.</div>
                ) : (
                  languageBreakdown.map((entry) => {
                    const width = getBarWidth(entry.value, maxValue(languageBreakdown));
                    return (
                      <div key={entry.label} className="chart-bar-row">
                        <span>{getLocalizedName(entry)}</span>
                        <div className="chart-bar-track">
                          {Number(entry.value || 0) > 0 ? (
                            <div className="chart-bar-fill theme-bar-mint" style={{ width: `${width}%` }} />
                          ) : null}
                        </div>
                        <strong>{entry.value}</strong>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Professional Motivations Distribution</h3>
              </div>
              <div className="chart-bars">
                {motivationBreakdown.length === 0 ? (
                  <div className="admin-empty-state compact">No motivation data yet.</div>
                ) : (
                  motivationBreakdown.map((entry) => {
                    const width = getBarWidth(entry.value, maxValue(motivationBreakdown));
                    return (
                      <div key={entry.label} className="chart-bar-row">
                        <span>{getLocalizedName(entry)}</span>
                        <div className="chart-bar-track">
                          {Number(entry.value || 0) > 0 ? (
                            <div className="chart-bar-fill theme-bar-gold" style={{ width: `${width}%` }} />
                          ) : null}
                        </div>
                        <strong>{entry.value}</strong>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Member vs. Non-Member Event Attendance Ratio</h3>
              </div>
              {attendancePie.length === 0 ? (
                <div className="admin-empty-state compact">No attendance ratio data yet.</div>
              ) : (
                <div className="dashboard-pie-section">
                  <div className="dashboard-pie-chart" style={buildPieStyle(attendancePie)} />
                  <div className="dashboard-pie-legend">
                    {attendancePie.map((entry, index) => (
                      <div key={entry.label} className="dashboard-pie-legend-item">
                        <span className="dashboard-pie-swatch" style={{ background: PIE_COLORS[index % PIE_COLORS.length] }} />
                        <span>{entry.label}</span>
                        <strong>{entry.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="dashboard-panel">
              <div className="dashboard-panel-header">
                <h3>Geographic Distribution</h3>
              </div>
              <div className="dashboard-mini-grid">
                <div>
                  <h4>Professionals by canton</h4>
                  {professionalsByCanton.length === 0 ? (
                    <div className="admin-empty-state compact">No canton data.</div>
                  ) : (
                    <div className="dashboard-pie-section small">
                      <div className="dashboard-pie-chart small" style={buildPieStyle(professionalsByCanton)} />
                      <div className="dashboard-pie-legend compact">
                        {professionalsByCanton.map((entry, index) => (
                          <div key={entry.label} className="dashboard-pie-legend-item">
                            <span className="dashboard-pie-swatch" style={{ background: PIE_COLORS[index % PIE_COLORS.length] }} />
                            <span>{entry.label}</span>
                            <strong>{entry.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h4>Users by state</h4>
                  {usersByState.length === 0 ? (
                    <div className="admin-empty-state compact">No state data.</div>
                  ) : (
                    <div className="dashboard-pie-section small">
                      <div className="dashboard-pie-chart small" style={buildPieStyle(usersByState)} />
                      <div className="dashboard-pie-legend compact">
                        {usersByState.map((entry, index) => (
                          <div key={entry.label} className="dashboard-pie-legend-item">
                            <span className="dashboard-pie-swatch" style={{ background: PIE_COLORS[index % PIE_COLORS.length] }} />
                            <span>{entry.label}</span>
                            <strong>{entry.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
